/**
 * Allowance claims drafted for a finished trip, pure (plan §5.7, EC-5, EC-39; TD-4.4, TD-4.16).
 *
 * After the trip the traveller is offered ready-made claims priced by the policy that applied on
 * the trip's start date (D12), with the same entitlement rows the estimate used:
 *
 *  - PER DIEM and LODGING IN LIEU come from ONE `computePerDiem` call, so the draft IS that output:
 *    the per-diem claim is `allowanceTotal − mealDeductionTotal`, the lodging-in-lieu claim is
 *    `lodgingInLieuTotal`, and together they are `total`. All of EC-39 lives in `computePerDiem`:
 *    the tier of the city where each night is spent, half days by departure / return time,
 *    first/last-day percentages, meals provided, lodging in lieu, personal days excluded (EC-5).
 *  - MILEAGE is one claim per own-vehicle entry: distance × the grade's mileage rate for the tier of
 *    the city it starts from. Entries on a personal day, or outside the trip ± the claim window
 *    (D32), are left out with a warning.
 *
 * The draft is advisory until the API files it: the API recomputes it from stored data, never from
 * amounts a client sends.
 */

import { Decimal } from 'decimal.js';
import type { TravelAllowanceClaimKind, TravelCityTier } from '../enums.js';
import { addDays } from './dates.js';
import { TRAVEL_CLAIM_WINDOW_DAYS } from './claim-link.js';
import { toMoneyString } from './money.js';
import { computePerDiem, MONEY_SCALE, type PerDiemMealsProvided, type PerDiemResult } from './per-diem.js';
import {
  entitlementRatesFor,
  entitlementRowsForGrade,
  resolveCityTier,
  type TravelPolicySnapshot,
} from './policy.js';

export interface AllowanceDraftStay {
  city: string;
  checkIn: string;
  checkOut: string;
  /** The tier the stay already carries; otherwise looked up from `city`. */
  cityTier?: TravelCityTier | null;
  lodgingInLieu?: boolean;
}

export interface AllowanceDraftTrip {
  /** The dates as travelled (after any cut-short or extension). */
  startDate: string;
  endDate: string;
  departureTime?: string | null;
  returnTime?: string | null;
  /** On the trip's start date; null when the traveller had none (EC-49: lowest band). */
  gradeRank: number | null;
  stays: readonly AllowanceDraftStay[];
  /** A day trip's destination, for the tier of a day with no night. */
  destinationCity?: string | null;
  personalDays: readonly string[];
}

export interface MileageEntry {
  date: string;
  fromCity: string;
  toCity: string;
  /** Kilometres, a decimal string > 0. */
  distanceKm: string;
}

export interface AllowanceDraftInput {
  trip: AllowanceDraftTrip;
  policy: TravelPolicySnapshot;
  mealsProvided?: readonly PerDiemMealsProvided[];
  mileage?: readonly MileageEntry[];
  /** What already has a live claim on the trip: those kinds (and mileage days) are not drafted again. */
  alreadyClaimed?: {
    perDiem?: boolean;
    lodgingInLieu?: boolean;
    mileageDates?: readonly string[];
  };
}

export interface AllowanceClaimDraft {
  kind: TravelAllowanceClaimKind;
  amount: string;
  currency: string;
  /** The claim's expense date: the trip's first day, or the mileage entry's day. */
  expenseDate: string;
  /** The span a per-diem / lodging claim covers (`perDiemStartDate` / `perDiemEndDate`). */
  periodStart: string;
  periodEnd: string;
  /** Mileage only. */
  distanceKm: string | null;
  /** Mileage only: the rate per km applied. */
  ratePerKm: string | null;
  /** A one-line label for the claim's business purpose ("Per diem, 3–5 Oct"). */
  description: string;
}

export type AllowanceDraftWarningCode =
  | 'grade_missing'
  | 'per_diem_not_configured'
  | 'rate_missing'
  | 'mileage_rate_missing'
  | 'mileage_outside_trip'
  | 'mileage_on_personal_day'
  | 'already_claimed';

export interface AllowanceDraftWarning {
  code: AllowanceDraftWarningCode;
  kind: TravelAllowanceClaimKind | null;
  dates: string[];
  message: string;
}

export interface AllowanceDraft {
  currency: string;
  gradeMissing: boolean;
  /** Exactly `computePerDiem`'s output; null when the policy has no per diem for the grade. */
  perDiem: PerDiemResult | null;
  claims: AllowanceClaimDraft[];
  warnings: AllowanceDraftWarning[];
}

function span(start: string, end: string): string {
  return start === end ? start : `${start} to ${end}`;
}

export function buildAllowanceDraft(input: AllowanceDraftInput): AllowanceDraft {
  const { trip, policy } = input;
  const currency = policy.currency;
  const warnings: AllowanceDraftWarning[] = [];
  const claims: AllowanceClaimDraft[] = [];
  const claimed = input.alreadyClaimed ?? {};

  const { rows, gradeMissing } = entitlementRowsForGrade(policy.entitlements, trip.gradeRank);
  if (gradeMissing) {
    warnings.push({
      code: 'grade_missing',
      kind: null,
      dates: [],
      message: 'No grade on the trip start date: the lowest band was used.',
    });
  }

  // --- Per diem and lodging in lieu: one computePerDiem call ---
  let perDiem: PerDiemResult | null = null;
  const hasPerDiemRows = rows.some((r) => r.mode === 'per_diem');
  if (policy.perDiemRule === null || !hasPerDiemRows) {
    warnings.push({
      code: 'per_diem_not_configured',
      kind: 'per_diem',
      dates: [],
      message: 'The travel policy has no per diem for this grade.',
    });
  } else {
    perDiem = computePerDiem({
      startDate: trip.startDate,
      endDate: trip.endDate,
      departureTime: trip.departureTime,
      returnTime: trip.returnTime,
      stays: trip.stays.map((s) => ({
        checkIn: s.checkIn,
        checkOut: s.checkOut,
        cityTier: s.cityTier ?? resolveCityTier(s.city, policy.cityTiers),
        lodgingInLieu: s.lodgingInLieu,
      })),
      dayTripCityTier: trip.destinationCity ? resolveCityTier(trip.destinationCity, policy.cityTiers) : null,
      personalDays: trip.personalDays,
      mealsProvided: input.mealsProvided,
      rule: policy.perDiemRule,
      perDiemRates: entitlementRatesFor(rows, 'per_diem'),
      lodgingInLieuRates: entitlementRatesFor(rows, 'lodging_in_lieu'),
      currency,
    });

    const missing = perDiem.days.filter((d) => d.rateMissing).map((d) => d.date);
    if (missing.length > 0) {
      warnings.push({
        code: 'rate_missing',
        kind: 'per_diem',
        dates: missing,
        message: 'The policy has no per-diem rate for the city tier of these days; they pay nothing.',
      });
    }

    const perDiemAmount = new Decimal(perDiem.allowanceTotal).sub(perDiem.mealDeductionTotal);
    const lodgingAmount = new Decimal(perDiem.lodgingInLieuTotal);
    const range = span(trip.startDate, trip.endDate);

    if (perDiemAmount.gt(0)) {
      if (claimed.perDiem) {
        warnings.push({
          code: 'already_claimed',
          kind: 'per_diem',
          dates: [],
          message: 'Per diem is already claimed for this trip.',
        });
      } else {
        claims.push({
          kind: 'per_diem',
          amount: perDiemAmount.toFixed(MONEY_SCALE),
          currency,
          expenseDate: trip.startDate,
          periodStart: trip.startDate,
          periodEnd: trip.endDate,
          distanceKm: null,
          ratePerKm: null,
          description: `Per diem, ${range}`,
        });
      }
    }
    if (lodgingAmount.gt(0)) {
      if (claimed.lodgingInLieu) {
        warnings.push({
          code: 'already_claimed',
          kind: 'lodging_in_lieu',
          dates: [],
          message: 'Lodging in lieu is already claimed for this trip.',
        });
      } else {
        claims.push({
          kind: 'lodging_in_lieu',
          amount: lodgingAmount.toFixed(MONEY_SCALE),
          currency,
          expenseDate: trip.startDate,
          periodStart: trip.startDate,
          periodEnd: trip.endDate,
          distanceKm: null,
          ratePerKm: null,
          description: `Lodging in lieu, ${range}`,
        });
      }
    }
  }

  // --- Mileage: one claim per entry ---
  const entries = input.mileage ?? [];
  if (entries.length > 0) {
    const mileageRates = entitlementRatesFor(rows, 'mileage');
    const personal = new Set(trip.personalDays);
    const claimedDays = new Set(claimed.mileageDates ?? []);
    const from = addDays(trip.startDate, -TRAVEL_CLAIM_WINDOW_DAYS);
    const to = addDays(trip.endDate, TRAVEL_CLAIM_WINDOW_DAYS);
    const outside: string[] = [];
    const onPersonal: string[] = [];
    const noRate: string[] = [];
    const repeated: string[] = [];

    for (const entry of entries) {
      if (entry.date < from || entry.date > to) {
        outside.push(entry.date);
        continue;
      }
      if (personal.has(entry.date)) {
        onPersonal.push(entry.date);
        continue;
      }
      if (claimedDays.has(entry.date)) {
        repeated.push(entry.date);
        continue;
      }
      const tier = resolveCityTier(entry.fromCity, policy.cityTiers);
      const rate = mileageRates[tier];
      if (rate === undefined || rate === null) {
        noRate.push(entry.date);
        continue;
      }
      const distance = new Decimal(entry.distanceKm);
      if (!distance.gt(0)) continue;
      const amount = distance.mul(rate).toDecimalPlaces(MONEY_SCALE, Decimal.ROUND_HALF_UP);
      claims.push({
        kind: 'mileage',
        amount: amount.toFixed(MONEY_SCALE),
        currency,
        expenseDate: entry.date,
        periodStart: entry.date,
        periodEnd: entry.date,
        distanceKm: toMoneyString(distance),
        ratePerKm: toMoneyString(rate),
        description: `Mileage ${entry.fromCity} to ${entry.toCity}, ${toMoneyString(distance)} km`,
      });
    }

    const warn = (code: AllowanceDraftWarningCode, dates: string[], message: string): void => {
      if (dates.length > 0) warnings.push({ code, kind: 'mileage', dates, message });
    };
    warn('mileage_outside_trip', outside, 'These days are outside the trip and its claim window.');
    warn('mileage_on_personal_day', onPersonal, 'Personal days carry no mileage.');
    warn('already_claimed', repeated, 'Mileage is already claimed for these days.');
    warn('mileage_rate_missing', noRate, 'The policy has no mileage rate for this grade.');
  }

  return { currency, gradeMissing, perDiem, claims, warnings };
}
